// The language of the page someone came from (?doclang=, added by the static
// pages' order and quote links: tools/lang-context.js). The order and quote
// checkouts start with it as the language to translate from when nothing is
// chosen yet, and list it first under "Most requested"
// (Hooks/useFrequentLanguages.js).
//
// Kept for the visit (sessionStorage), so it still applies after the visitor
// looks around the site before ordering. A later page with another language
// replaces it.
//
// This file runs on every page load (App.js), so it does not import the
// language list: the value is only checked for shape here, and the checkout
// ignores one it does not sell.

export const PARAM = 'doclang';
const STORAGE_KEY = 'languageContext';
// Set once the visitor picks a language in a checkout themselves: from then on
// the article's language is never put back in their place, even on a step
// opened again later in the visit. A new article (a new ?doclang=) clears it.
const CHOSEN_KEY = 'languageContextChosen';

// "korean" -> "korean". One value; from a list, the first that looks like a
// language identifier. English is the other side of every pair, never this one.
export const parseLanguageContext = (raw) => String(raw || '').split(',')
  .map((part) => part.trim())
  .find((value) => /^[A-Za-z]{2,30}$/.test(value) && value !== 'english') || '';

export const storedLanguageContext = () => {
  try {
    return parseLanguageContext(window.sessionStorage.getItem(STORAGE_KEY));
  } catch (e) {
    return '';
  }
};

// Called once on load (App.js). Remembers ?doclang= and takes it out of the
// address, leaving every other parameter and the hash as they were.
export const captureLanguageContextFromUrl = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    if (!params.has(PARAM)) {
      return null;
    }
    const value = parseLanguageContext(params.get(PARAM));
    if (value) {
      try {
        window.sessionStorage.setItem(STORAGE_KEY, value);
        window.sessionStorage.removeItem(CHOSEN_KEY);
      } catch (e) {
        // Blocked storage: the checkout just starts empty, as before.
      }
    }
    params.delete(PARAM);
    const search = params.toString();
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`);
    return value;
  } catch (e) {
    return null;
  }
};

// The visitor picked a language in the checkout (OrderDocuments, QuoteDetails).
export const markLanguageChosen = () => {
  try {
    window.sessionStorage.setItem(CHOSEN_KEY, '1');
  } catch (e) {
    // Blocked storage: nothing was stored to preselect either.
  }
};

// The language to start a checkout with: the article's, unless the visitor has
// already picked one themselves during this visit.
export const languageToPreselect = () => {
  try {
    return window.sessionStorage.getItem(CHOSEN_KEY) ? '' : storedLanguageContext();
  } catch (e) {
    return '';
  }
};

// `popular` with the context's language first: added if missing, moved to the
// top if already there.
export const withLanguageContext = (popular, context = storedLanguageContext()) => (context
  ? [context, ...popular.filter((value) => value !== context)]
  : popular);
